/**
 * Module dependencies.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { Trash2 } from 'lucide-react';
import { appRoutes } from 'app/routes';
import { useDeletePoolInvestProject, usePoolInvestProject } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { PoolValuationPanel } from './pool-valuation-panel';
import { PoolParticipationPanel } from './pool-participation-panel';

/**
 * Export `PoolInvestProjectDetail` component.
 *
 * Project shell — participants and buckets. Movements and the per-person
 * overview land in later installments.
 */

export function PoolInvestProjectDetail(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { success, error } = useToast();
  const { data: project, isLoading } = usePoolInvestProject(projectId);
  const remove = useDeletePoolInvestProject();

  const onDelete = async () => {
    if (!projectId) {
      return;
    }

    try {
      await remove.mutateAsync(projectId);
      success({ message: translate('poolInvest.project.deleted') });
      navigate(appRoutes.dashboard.poolInvest.base);
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  if (isLoading || !project) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={'w-full space-y-6'}>
      <div className={'flex flex-wrap items-center justify-between gap-3'}>
        <div>
          <Title>{project.name}</Title>
          {project.description ? <div className={'text-sm text-muted-foreground'}>{project.description}</div> : null}
        </div>

        {project.isOwner ? (
          <Button disabled={remove.isLoading} onClick={onDelete} variant={'outline'}>
            <Trash2 className={'h-4 w-4'} />
            {translate('poolInvest.project.delete')}
          </Button>
        ) : null}
      </div>

      {projectId ? <PoolValuationPanel projectId={projectId} /> : null}

      {projectId ? <PoolParticipationPanel projectId={projectId} /> : null}

      <div className={'grid gap-6 md:grid-cols-2'}>
        {/* Participants */}
        <section className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
          <h2 className={'mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
            {translate('poolInvest.labels.participants')}
          </h2>

          <ul className={'space-y-2'}>
            {project.participants.map(participant => (
              <li className={'flex items-center justify-between gap-3 text-sm'} key={participant.id}>
                <span className={'text-foreground'}>{participant.name || participant.email}</span>

                <span className={'flex items-center gap-2 text-muted-foreground'}>
                  <span className={'tabular-nums'}>
                    {'×'}
                    {participant.weight}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Buckets */}
        <section className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
          <h2 className={'mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
            {translate('poolInvest.labels.buckets')}
          </h2>

          <ul className={'space-y-2'}>
            {project.buckets.map(bucket => (
              <li className={'flex items-center justify-between gap-3 text-sm'} key={bucket.id}>
                <span className={'text-foreground'}>{bucket.name}</span>

                <Badge variant={'outline'}>
                  {translate(`poolInvest.bucketKind.${bucket.kind}`)}
                  {bucket.assetSymbol ? ` · ${bucket.assetSymbol}` : ''}
                </Badge>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
